import type { Message, MessageVersion } from "sdk";
import { isMarkdown } from "@/components/features/chat/Message";
import { generateNotificationIcon } from "@/lib/drawing";
import { notificationPermission } from "@/lib/notifications";
import { stripMarkdownAndResolveMentions as stripMarkdownAndResolveMentionsOriginal } from "@/lib/notifications/util";
import type { RootStore } from "../core/Store";

function getContentFromVersion(latestVersion: MessageVersion): string {
	if (isMarkdown(latestVersion.type)) {
		return latestVersion.content ?? "";
	}
	return "";
}

export class NotificationService {
	constructor(private store: RootStore) {}

	async handleMessageCreate(m: Message) {
		const me = this.store.users.get("@self");
		let is_mentioned = false;
		const latestVersion = m.latest_version;
		const mentions = latestVersion?.mentions;
		const isMarkdownMsg = latestVersion
			? isMarkdown(latestVersion.type)
			: false;
		const content = latestVersion ? getContentFromVersion(latestVersion) : "";

		// Determine if mentioned
		if (me && m.author_id !== me.id && isMarkdownMsg && mentions) {
			if (mentions.users?.some((u) => u.id === me.id)) {
				is_mentioned = true;
			}
			if (!is_mentioned && mentions.everyone) {
				is_mentioned = true;
			}
			if (!is_mentioned && mentions.roles && mentions.roles.length > 0) {
				const channel = this.store.channels.get(m.channel_id);
				if (channel?.room_id) {
					const room_member = this.store.roomMembers.get(
						`${channel.room_id}:${me.id}`,
					);
					if (room_member && mentions.roles) {
						for (const role of mentions.roles) {
							if (room_member.roles.some((r) => r === role.id)) {
								is_mentioned = true;
								break;
							}
						}
					}
				}
			}
		}

		// We need access to preferences. For now, assume we can get them from store or global
		// Since we haven't fully refactored preferences into RootStore, we might need a bridge
		// or just read from the legacy location if available?
		// Or better, let's inject a preferences accessor into RootStore.

		const preferences = this.store.preferences.read();
		if (!preferences) return;

		if (
			is_mentioned &&
			notificationPermission() === "granted" &&
			preferences.frontend.desktop_notifs
		) {
			const author = this.store.users.get(m.author_id);
			const channel = this.store.channels.get(m.channel_id);
			const title = `${author?.name ?? "Someone"} in #${
				channel?.name ?? "channel"
			}`;
			const rawContent = content;

			// Helper wrapper for the util
			const processedContent = await stripMarkdownAndResolveMentionsOriginal(
				rawContent,
				m.channel_id,
				this.store,
				mentions,
			);
			const body = processedContent.substring(0, 200);

			(async () => {
				let icon: string | undefined;
				if (author) {
					const room = channel?.room_id
						? this.store.rooms.get(channel.room_id)
						: undefined;
					const iconBlob = await generateNotificationIcon(
						author,
						room ?? undefined,
					);
					if (iconBlob) {
						icon = URL.createObjectURL(iconBlob);
					}
				}

				const notification = new Notification(title, { body, icon });
				notification.onclick = () => {
					window.focus();
					location.href = `/channel/${m.channel_id}/message/${m.id}`;
				};
				if (icon) {
					notification.onclose = () => {
						URL.revokeObjectURL(icon);
					};
				}
			})();
		}

		// TTS notifications
		const ttsEnabled = preferences.frontend.tts_notifs;
		const ttsMode = preferences.notifs.tts;
		const shouldSpeak =
			ttsEnabled &&
			ttsMode !== "Nothing" &&
			(ttsMode === "Always" || (ttsMode === "Mentions" && is_mentioned));
		const isOwnMessage = m.author_id === me?.id;

		if (shouldSpeak && !isOwnMessage && isMarkdownMsg) {
			const author = this.store.users.get(m.author_id);
			const channel = this.store.channels.get(m.channel_id);
			const rawContent = content;
			const processedContent = await stripMarkdownAndResolveMentionsOriginal(
				rawContent,
				m.channel_id,
				this.store,
				mentions,
			);
			const text = processedContent.substring(0, 200);

			const utterance = new SpeechSynthesisUtterance(
				`${author?.name ?? "Someone"} in #${
					channel?.name ?? "channel"
				} says: ${text}`,
			);
			window.speechSynthesis.speak(utterance);
		}
	}
}
